import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    inject,
} from '@angular/core';
import {
    AbstractControl,
    ControlContainer,
    UntypedFormGroup,
} from '@angular/forms';
import { LocaleFacadeService } from '@wdx/clmi/api-services/services';
import { DocumentType, StatusType } from '@wdx/clmi/clmi-swagger-gen';
import { DocumentTemplateExt } from '@wdx/clmi/utils/models';
import {
    TRANSLATION_DOC_CHECKLIST_DOC_TEMPLATE,
    TRANSLATION_DOC_CHECKLIST_DOC_TYPE,
    WdxDestroyClass,
} from '@wdx/shared/utils';
import { Observable } from 'rxjs';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { FeatureSvg } from '../../../../../models/svg.model';
import {
    DocumentTypesService,
    DocumentsFacadeService,
} from '../../shared/services';

@Component({
    selector: 'clmi-generate-document-template-form',
    templateUrl: './generate-document-template-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class GenerateDocumentTemplateFormComponent
    extends WdxDestroyClass
    implements OnInit
{
    @Input() documentRequirementId: string;
    @Input() documentTypeCodes: string[];

    documentTypes$: Observable<DocumentType[]>;
    documentTemplates$: Observable<DocumentTemplateExt[]>;
    form: UntypedFormGroup;

    readonly FEATURE_SVG = FeatureSvg;
    readonly TRANSLATION_DOC_CHECKLIST_DOC_TYPE =
        TRANSLATION_DOC_CHECKLIST_DOC_TYPE;
    readonly TRANSLATION_DOC_CHECKLIST_DOC_TEMPLATE =
        TRANSLATION_DOC_CHECKLIST_DOC_TEMPLATE;

    public controlContainer = inject(ControlContainer);
    private documentsFacadeService = inject(DocumentsFacadeService);
    private documentTypesService = inject(DocumentTypesService);
    private localeFacadeService = inject(LocaleFacadeService);

    get documentTypeCodeControl(): AbstractControl {
        return this.form?.get('documentTypeCode');
    }

    get documentTemplateIdControl(): AbstractControl {
        return this.form?.get('documentTemplateId');
    }

    ngOnInit(): void {
        this.form = (this.controlContainer as any).form?.get(
            this.documentRequirementId,
        );

        this.documentTypes$ =
            this.documentTypesService.getDocumentTypesFromDocumentTypeCodes(
                this.documentTypeCodes,
            );

        if (this.documentTypeCodeControl.value) {
            this.documentTemplates$ = this.getTemplates$(
                this.documentTypeCodeControl.value,
            );
        }

        this.documentTypeCodeControl.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((documentTypeCode) => {
                this.documentTemplateIdControl.setValue(null);
                this.documentTemplates$ = this.getTemplates$(documentTypeCode);
            });

        this.documentTypes$.pipe(take(1)).subscribe((documentTypes) => {
            if (documentTypes.length === 1) {
                this.documentTypeCodeControl.setValue(documentTypes[0].code);
            }
        });
    }

    getTemplates$(documentTypeCode: string) {
        return this.documentsFacadeService
            .getDocumentTemplates$(documentTypeCode)
            .pipe(
                map((documentTemplates) => {
                    return documentTemplates
                        ?.filter((dt) => dt.status === StatusType.Active)
                        .map((documentTemplate) => ({
                            ...documentTemplate,
                            languageFullName:
                                this.localeFacadeService.getLocale(
                                    documentTemplate.locale?.code,
                                )?.nativeName,
                        }));
                }),
                tap((documentTemplates) => {
                    if (documentTemplates?.length === 1) {
                        this.documentTemplateIdControl.setValue(
                            documentTemplates[0].id,
                        );
                    }
                }),
            );
    }
}
